import { useMemo, useState } from 'react'
import { usePhones, useRelay, RelayContext, logout } from '../api'
import { NotificationProvider, useNotify } from '../components/Notifications'
import { PhonePicker } from '../components/PhonePicker'
import { PhoneCallView } from '../components/PhoneCallView'
import { OperatorSidebar } from '../components/OperatorSidebar'
import { OperatorBottomNav, type OperatorTab } from '../components/OperatorBottomNav'
import { CallHistoryPanel } from '../components/CallHistoryPanel'
import { CallBoostPanel } from '../components/CallBoostPanel'
import { Modal } from '../components/Modal'
import { useIsDesktop } from '../hooks/useIsDesktop'
import { useCallToneSubscriber } from '../hooks/useCallToneSubscriber'

export function UserCallPage() {
  return (
    <NotificationProvider>
      <UserCallInner />
    </NotificationProvider>
  )
}

function UserCallInner() {
  const notify = useNotify()
  const relay = useRelay(notify)
  useCallToneSubscriber()
  return (
    <RelayContext.Provider value={relay}>
      <UserCallScreen />
    </RelayContext.Provider>
  )
}

function UserCallScreen() {
  const { data: phones, isLoading, error } = usePhones()
  const sortedPhones = useMemo(
    () => (phones ?? []).slice().sort((a, b) => a.name.localeCompare(b.name)),
    [phones],
  )

  const defaultId = useMemo(() => {
    const online = sortedPhones.find(p => p.status !== 'offline')
    return online?.id ?? sortedPhones[0]?.id ?? null
  }, [sortedPhones])
  const [activeId, setActiveId] = useState<string | null>(null)
  const effectiveActiveId = activeId ?? defaultId
  const activePhone = sortedPhones.find(p => p.id === effectiveActiveId)

  // Lock the PhonePicker during an active call so the user can't swap
  // phones mid-call. `isInCall` derives from the active phone's `status`
  // instead of propagating state from PhoneCallView — simpler, single
  // source of truth (react-query cache).
  const isInCall = activePhone?.status === 'in-call' || Boolean(activePhone?.currentCall)

  const isDesktop = useIsDesktop()
  const variant: 'desktop' | 'mobile' = isDesktop ? 'desktop' : 'mobile'
  const [tab, setTab] = useState<OperatorTab>('dialer')
  const [settingsOpen, setSettingsOpen] = useState(false)

  // Derived: during an active call the UI snaps to dialer regardless of the
  // user's last tab choice. After hangup `activeTab` falls back to `tab` —
  // the user returns to whatever they were looking at before the call.
  // Derived instead of useEffect to avoid cascading renders.
  const activeTab: OperatorTab = isInCall ? 'dialer' : tab

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <p className="text-sm text-gray-500">Загрузка...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-950">
        <p className="text-sm text-red-500">Ошибка загрузки трубок</p>
      </div>
    )
  }

  if (sortedPhones.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-950 px-4 text-center">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Трубки не привязаны
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Свяжитесь с администратором, чтобы он привязал SIM-трубки к вашему ключу.
        </p>
        <button
          onClick={() => logout()}
          className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          Выйти
        </button>
      </div>
    )
  }

  const handlePickInPhonesTab = (id: string) => {
    setActiveId(id)
    setTab('dialer')
  }

  // Single shared <PhoneCallView /> — never rendered twice. Visibility on
  // mobile is controlled via CSS; variant switches via JS on resize without
  // remount, so usePhoneCall / PC survive the transition.
  const showCallView = isDesktop || activeTab === 'dialer'

  return (
    <div className="h-screen flex bg-gray-50 dark:bg-gray-950 overflow-hidden">
      <OperatorSidebar
        phones={sortedPhones}
        activeId={effectiveActiveId}
        onPick={setActiveId}
        locked={isInCall}
        onLogout={() => logout()}
      />

      <main className="flex-1 flex flex-col min-w-0">
        {/* Mobile top-bar with gear icon — CSS-hidden on desktop */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <h1 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            МММ
          </h1>
          <button
            type="button"
            aria-label="Настройки"
            onClick={() => setSettingsOpen(true)}
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
          </button>
        </header>

        {/* Shared PhoneCallView — always mounted, visibility toggled on mobile */}
        <div className={showCallView ? 'flex-1 flex flex-col min-h-0' : 'hidden'}>
          {effectiveActiveId && (
            <PhoneCallView
              key={effectiveActiveId}
              phoneId={effectiveActiveId}
              variant={variant}
              hideHeader={isDesktop}
            />
          )}
        </div>

        {/* Mobile-only alternative tabs. Rendered only when mobile and not on
            dialer — avoids double-render of heavy content. */}
        {!isDesktop && activeTab === 'phones' && (
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <p className="px-1 pb-2 text-[11px] font-mono uppercase tracking-wider text-gray-500">
              Трубки ({sortedPhones.length})
            </p>
            <PhonePicker
              phones={sortedPhones}
              activeId={effectiveActiveId}
              onPick={handlePickInPhonesTab}
              locked={isInCall}
              layout="vertical"
            />
          </div>
        )}

        {!isDesktop && activeTab === 'history' && effectiveActiveId && (
          <div className="flex-1 min-h-0">
            <CallHistoryPanel phoneId={effectiveActiveId} variant="mobile" />
          </div>
        )}

        {/* Bottom nav — hidden on desktop, hidden during an active call */}
        {!isDesktop && !isInCall && (
          <OperatorBottomNav tab={activeTab} onTabChange={setTab} />
        )}
      </main>

      {/* Right-side history panel — desktop only (≥768px). Mobile gets
          the same component inside the `history` bottom-nav tab. md:flex
          (not lg:flex) avoids a 768-1023 dead zone where useIsDesktop is
          true (so BottomNav is hidden) but the panel would also be
          missing — see plan §C8. */}
      {isDesktop && effectiveActiveId && (
        <aside className="hidden md:flex w-72 lg:w-[360px] border-l border-gray-200 dark:border-gray-800 shrink-0">
          <CallHistoryPanel phoneId={effectiveActiveId} variant="desktop" />
        </aside>
      )}

      <Modal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        title="Настройки"
      >
        <div className="space-y-4">
          <CallBoostPanel controlsTestId="mobile-settings-boost" />

          <div className="pt-3 border-t border-gray-200 dark:border-gray-800">
            <button
              type="button"
              onClick={() => logout()}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Выйти
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
